import { Component, OnInit } from '@angular/core';
import { childMetaDataObj, DataConnectionObj } from '../../models/data-connection';
import { UserManagementService } from '../../../shared/services/user-management.service';
import { ApiService } from '../../../shared/services/api.service';
import {
  ConnectionStatus,
  DataConnectionActions as DataConnectionActionsEnum,
  DataConnectionActionsMutation,
  DataConnectionActionsMutationVariables,
  GetDataConnectionListQuery,
  GetDataConnectionListQueryVariables,
  SyncStatus,
} from '../../../../API';
import { GetDataConnectionList } from '../../../../graphql/queries';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AuthService } from '../../../shared/services/auth.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DataConnectionActions } from '../../../../graphql/mutations';

@Component({
  selector: 'app-data-connection',
  templateUrl: './data-connection.component.html',
  styleUrl: './data-connection.component.scss',
})
export class DataConnectionComponent implements OnInit {
  dataConnectionListData!: DataConnectionObj[];
  isLoading: boolean = false;
  readonly createDataConnectionModule: string[] = [
    'app.dcr-portal.data-connection.action.create',
    'app.dcr-portal.data-connection.action.edit',
  ];
  dataConnectionActions = DataConnectionActionsEnum;
  readonly hasDataConnectionActionEntitlement: string[] = ['app.dcr-portal.data-connection.action.delete'];

  constructor(
    private userManagementService: UserManagementService,
    private apiService: ApiService,
    private nzMessageService: NzMessageService,
    public authService: AuthService,
    private modalService: NzModalService,
  ) {}

  ngOnInit(): void {
    this.getDataConnections();
  }

  getDataConnections(): void {
    this.isLoading = true;
    this.apiService
      .query<GetDataConnectionListQuery, GetDataConnectionListQueryVariables>(GetDataConnectionList, {
        orgType: this.userManagementService.getOrgType(),
      })
      .then((response) => {
        const parentTableData = response.data?.GetDataConnectionList.dataConnectionList as any;
        const childTableData = JSON.parse(response.data?.GetDataConnectionList.metadataList as any);

        this.dataConnectionListData = parentTableData.map((item: DataConnectionObj) => {
          const childMetaData = childTableData.find(
            (childItem: childMetaDataObj) => childItem.dataConnectionId === item.connectionId,
          );
          item.metadataList = childMetaData && JSON.parse(childMetaData.metadata);
          item.expand = false;
          return item;
        });
      })
      .catch((error) => {
        this.nzMessageService.error(`Error Fetching Data Connection List ${error?.message}`);
      })
      .finally(() => (this.isLoading = false));
  }

  getColorCode(status: any): string | undefined {
    switch (status) {
      case SyncStatus.ACTIVE:
      case ConnectionStatus.ACTIVE:
      case ConnectionStatus.SUCCESS: {
        return 'success';
      }
      case ConnectionStatus.PENDING:
      case SyncStatus.PENDING: {
        return 'warning';
      }
      case ConnectionStatus.NOT_VERIFIED:
      case ConnectionStatus.INACTIVE:
      case ConnectionStatus.FAIL: {
        return 'error';
      }
    }
    return;
  }

  openDataConnectionActionsModal(
    dataConnection: DataConnectionObj,
    action: DataConnectionActionsEnum,
    tableName: string = '',
  ): void {
    const actionMessage = this.getActionMessage(action, tableName);
    this.modalService.confirm({
      nzTitle: `Are you sure you want to ${action.toLowerCase()} this record?`,
      nzContent: actionMessage,
      nzOkText: `Yes, ${action.toLowerCase()}`,
      nzOnOk: () => this.handleAction(dataConnection, action, tableName),
      nzCancelText: 'Cancel',
    });
  }

  getActionMessage(action: DataConnectionActionsEnum, table: string): string {
    switch (action) {
      case DataConnectionActionsEnum.DEACTIVATE:
        return 'Deactivating revokes table access in the Query interface. Workflows and other downstream processes that rely on these tables will be impacted until the connection is restored.';
      case DataConnectionActionsEnum.DELETE:
        return 'Deleting will permanently remove access to its associated tables and metadata. Dependent workflows and queries will no longer function. This action cannot be undone.';
      case DataConnectionActionsEnum.REACTIVATE:
        if (table) {
          return 'Reactivating restores connection to the original table. Table access for queries and workflows will resume once data is linked to the collaboration.';
        }
        return 'Reactivating restores connection to the original data source. Table access for queries and workflows will resume once data is linked to the collaboration.';
      default:
        return 'Invalid action.';
    }
  }

  handleAction(dataConnection: DataConnectionObj, action: DataConnectionActionsEnum, tableName: string = ''): void {
    this.isLoading = true;
    const { connectionId, orgType } = dataConnection;
    this.apiService
      .query<DataConnectionActionsMutation, DataConnectionActionsMutationVariables>(DataConnectionActions, {
        dataConnectionActionsInput: {
          action: action,
          dataConnectionId: connectionId,
          orgType: orgType,
          table_name: tableName ? tableName : null,
        },
      })
      .then((data: any) => {
        if (data.data.DataConnectionActions.success) {
          const entityName = tableName ? tableName : dataConnection.connectionName;
          this.nzMessageService.success(
            `${entityName} ${action.toLowerCase()}d successfully for ${dataConnection.orgType}`,
          );
          this.getDataConnections();
        } else {
          this.nzMessageService.error(`Failed to ${action} record with error: ${data.data.DataConnectionActions.body}`);
        }
      })
      .catch((error) => {
        this.nzMessageService.error(`Failed to ${action} record with error: ${error?.body}`);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  sortByLastModifiedAt(a: DataConnectionObj, b: DataConnectionObj): number {
    if (!a.lastModifiedAt || !b.lastModifiedAt) {
      return 0;
    }
    return new Date(a.lastModifiedAt).getTime() - new Date(b.lastModifiedAt).getTime();
  }
}
